/**
 * Fly-by's half of the wheel (user request, 2026-08-20: "zoom to/fro the
 * plane in the fly-by camera with mouse wheel"). A sibling of
 * `scene-cockpit-report.ts` / `scene-propellers.ts` / `mission-report.ts` on
 * the same pattern — one frame-loop concern pulled out of `flight-scene.ts`,
 * which sits at its size gate.
 *
 * The wheel is the throttle lever everywhere else (`pointer.ts`, 2026-08-19).
 * This module is the routing: whenever `cameraMode` is literally `"flyBy"`,
 * the same `PointerFrame.throttleDelta` the throttle would otherwise consume
 * instead moves the fly-by camera's zoom goal, and the *sim* sees zero — a
 * camera-only control must stay invisible to the flight model and to a
 * Mission Recorder tape, exactly like every other view-only setting. Cockpit
 * and chase are untouched: the wheel stays throttle there.
 */

import { FLY_BY_ZOOM, flyByZoomGoalAfterWheel, FLY_BY_ZOOM_SMOOTH_RATE, smoothScalar } from "./camera-follow.ts";
import type { CameraMode } from "./camera-modes.ts";
import type { CameraRig } from "./flight-view.ts";
import type { PointerFrame } from "./pointer.ts";

/** The slice of `flight-scene.ts`'s `SceneState` this module reads and mutates. */
export interface FlyByZoomState {
  readonly cameraMode: CameraMode;
  readonly rig: CameraRig;
  flyByZoomGoal: number;
}

/**
 * The wheel delta the *sim* should advance the throttle by this frame — zero
 * while fly-by is consuming it for zoom, `pointer.throttleDelta` unchanged
 * otherwise. Also advances the zoom goal and its smoothed value in place, so
 * call this exactly once per frame, before `advanceThrottle` reads its
 * return value.
 */
export function routeWheelForFlyByZoom(state: FlyByZoomState, pointer: PointerFrame, dt: number): number {
  if (state.cameraMode !== "flyBy") return pointer.throttleDelta;
  state.flyByZoomGoal = flyByZoomGoalAfterWheel(state.flyByZoomGoal, pointer.throttleDelta);
  state.rig.flyByZoom = smoothScalar(state.rig.flyByZoom, state.flyByZoomGoal, FLY_BY_ZOOM_SMOOTH_RATE, dt);
  return 0;
}

/**
 * Resets the zoom to its default. Called when leaving fly-by mode
 * (`flight-scene.ts`'s `setCameraMode`) rather than persisted across a mode
 * switch — the least surprising choice: a return to the camera later starts
 * from the same familiar framing instead of wherever the wheel last left it,
 * and there is no on-screen zoom readout to remind a pilot what they left it
 * at.
 */
export function resetFlyByZoom(state: FlyByZoomState): void {
  state.flyByZoomGoal = FLY_BY_ZOOM.default;
  state.rig.flyByZoom = FLY_BY_ZOOM.default;
}
